import { accessControl } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { useCallback } from 'react';

import { flowsApi } from 'apis/flows';
import { notificationActions } from 'components/notifications/actions/notification';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

/**
 * Returns a callback to check whether there are conflicting dates within the
 * created schedules, and notifies the user if they have the required access.
 */
export const useScheduleConflictsNotice = (): (() => Promise<void>) => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  return useCallback(async () => {
    const hasBusinessHoursAccess = accessControl.hasAccess(
      'settings.business_hours',
    );

    if (!hasBusinessHoursAccess) return;

    const lsKey = 'scheduleConflictsTimestamp';

    try {
      const { data: response } = await dispatch(
        flowsApi.endpoints.readFlowsSchedulesConflicts.initiate(),
      );
      const conflictingSchedules = response?.data.schedules_in_conflicts ?? [];
      const { length: conflictsLength } = conflictingSchedules;
      const localConflictsTimestamp = localStorage.getItem(lsKey) ?? '';

      if (!conflictsLength) {
        if (!localConflictsTimestamp) {
          localStorage.removeItem(lsKey);
        }
        return;
      }

      const { notificationAdd, notificationRemoveByType } = notificationActions;

      if (
        !localConflictsTimestamp ||
        moment.utc().isAfter(moment.utc(localConflictsTimestamp))
      ) {
        const handleNotificationAction = (isClosed?: boolean) => () => {
          localStorage.setItem(
            lsKey,
            moment.utc().add(1, 'days').toISOString(),
          );
          dispatch(notificationRemoveByType({ type: lsKey }));

          if (isClosed) {
            ampli.businessHoursConflictNotificationClosed();
          } else {
            ampli.businessHoursConflictNotificationButtonClicked();
            navigate({
              path: '/settings/businesshours',
              params: {},
            });
          }
        };

        dispatch(
          notificationAdd({
            icon: 'exclamation',
            status: 'warning',
            leftAction: null,
            rightAction: {
              text: 'Go to Business hours',
              action: handleNotificationAction(),
            },
            closeAction: handleNotificationAction(true),
            title: 'Please review conflicting dates',
            text: `We found date conflicts in ${conflictsLength} ${
              conflictsLength === 1 ? 'schedule' : 'schedules'
            }. For each schedule, please resolve the conflicts.`,
            type: lsKey,
          }),
        );
      }
    } catch {
      throw new Error('An error occurred when fetching schedule conflicts!');
    }
  }, [dispatch, navigate]);
};
